import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Search, X } from 'lucide-react';
import { cn } from '../../lib/utils';
import { searchApi } from '../../lib/api';
import { useDebouncedValue } from '../../hooks/useDebouncedValue';
import { buildUnifiedGroups } from '../../lib/searchRegistry';

const NavbarSearch = () => {
  const navigate = useNavigate();
  const containerRef = useRef(null);
  const inputRef = useRef(null);

  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [backendGroups, setBackendGroups] = useState([]);
  const [loading, setLoading] = useState(false);
  const [activeIndex, setActiveIndex] = useState(-1);

  const debouncedQuery = useDebouncedValue(query, 280);
  const trimmedQuery = query.trim();

  const groups = useMemo(() => buildUnifiedGroups(debouncedQuery, backendGroups), [debouncedQuery, backendGroups]);
  const flatItems = useMemo(
    () => groups.filter((g) => !g.comingSoon).flatMap((g) => g.items.map((item) => ({ ...item, groupType: g.type }))),
    [groups]
  );
  const hasAnyResults = flatItems.length > 0;
  const hasComingSoonMatch = groups.some((g) => g.comingSoon);
  const showEmptyState = debouncedQuery.length > 0 && !loading && !hasAnyResults && !hasComingSoonMatch;

  useEffect(() => {
    const term = debouncedQuery.trim();
    if (!term) {
      setBackendGroups([]);
      setLoading(false);
      return;
    }
    let cancelled = false;
    setLoading(true);
    searchApi
      .search(term, 5)
      .then((data) => {
        if (!cancelled) setBackendGroups(data.groups || []);
      })
      .catch(() => {
        if (!cancelled) setBackendGroups([]);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [debouncedQuery]);

  useEffect(() => {
    setActiveIndex(-1);
  }, [debouncedQuery]);

  const closeSearch = () => {
    setOpen(false);
    setQuery('');
    setBackendGroups([]);
    setActiveIndex(-1);
  };

  const openSearch = () => {
    setOpen(true);
    requestAnimationFrame(() => inputRef.current?.focus());
  };

  const goToFullResults = (q = trimmedQuery, type) => {
    if (!q) return;
    const typeParam = type ? `&type=${type}` : '';
    navigate(`/search?q=${encodeURIComponent(q)}${typeParam}`);
    closeSearch();
  };

  const selectItem = (item) => {
    navigate(item.url);
    closeSearch();
  };

  useEffect(() => {
    const handleGlobalKeydown = (e) => {
      const isK = e.key === 'k' || e.key === 'K';
      if ((e.metaKey || e.ctrlKey) && isK) {
        e.preventDefault();
        setOpen(true);
        requestAnimationFrame(() => inputRef.current?.focus());
      }
    };
    document.addEventListener('keydown', handleGlobalKeydown);
    return () => document.removeEventListener('keydown', handleGlobalKeydown);
  }, []);

  useEffect(() => {
    if (!open) return;
    const handleClickOutside = (e) => {
      if (containerRef.current && !containerRef.current.contains(e.target)) {
        closeSearch();
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [open]);

  const handleInputKeyDown = (e) => {
    if (e.key === 'Escape') {
      e.preventDefault();
      closeSearch();
      return;
    }
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      if (flatItems.length > 0) setActiveIndex((i) => (i + 1) % flatItems.length);
      return;
    }
    if (e.key === 'ArrowUp') {
      e.preventDefault();
      if (flatItems.length > 0) setActiveIndex((i) => (i <= 0 ? flatItems.length - 1 : i - 1));
      return;
    }
    if (e.key === 'Enter') {
      e.preventDefault();
      if (activeIndex >= 0 && flatItems[activeIndex]) {
        selectItem(flatItems[activeIndex]);
      } else {
        goToFullResults();
      }
    }
  };

  return (
    <div className="flex items-center" ref={containerRef} data-testid="navbar-search">
      <button
        type="button"
        aria-label="Search"
        onClick={openSearch}
        className={cn(
          'h-9 w-9 flex items-center justify-center rounded-md text-white/60 hover:text-white transition-colors',
          open && 'opacity-0 pointer-events-none'
        )}
        data-testid="navbar-search-trigger"
      >
        <Search className="h-[18px] w-[18px]" strokeWidth={1.5} />
      </button>

      <div
        className={cn(
          'absolute right-0 top-1/2 -translate-y-1/2 overflow-hidden transition-[width] duration-200 ease-out z-30',
          open ? 'w-[calc(100vw-7rem)] max-w-xs sm:w-80' : 'w-9'
        )}
      >
        {open && (
          <div className="flex items-center bg-[#0f1420] border border-white/15 rounded-md focus-within:border-autoxium-blue focus-within:ring-1 focus-within:ring-autoxium-blue">
            <Search className="h-4 w-4 ml-3 text-white/40 shrink-0" strokeWidth={1.5} />
            <input
              ref={inputRef}
              type="text"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              onKeyDown={handleInputKeyDown}
              placeholder="Search articles, tools, simulations..."
              className="flex-1 min-w-0 bg-transparent text-sm text-white placeholder:text-white/40 px-2 py-2 outline-none"
              data-testid="navbar-search-input"
            />
            <button
              type="button"
              aria-label="Close search"
              onClick={closeSearch}
              className="h-9 w-9 flex items-center justify-center text-white/40 hover:text-white shrink-0"
              data-testid="navbar-search-close"
            >
              <X className="h-4 w-4" strokeWidth={1.5} />
            </button>
          </div>
        )}
      </div>

      {open && debouncedQuery.length > 0 && (
        <div
          className="absolute right-0 top-full mt-2 w-[calc(100vw-7rem)] max-w-xs sm:w-80 max-h-[70vh] overflow-y-auto bg-[#0f1420] border border-white/15 rounded-md shadow-xl z-30 text-white"
          data-testid="navbar-search-results"
        >
          {loading && flatItems.length === 0 && (
            <div className="px-4 py-6 text-sm text-white/50 text-center">Searching…</div>
          )}

          {!loading && showEmptyState && (
            <div className="px-4 py-6 text-sm text-white/60 text-center">
              No results for &lsquo;{debouncedQuery}&rsquo;. Try a different term.
            </div>
          )}

          {groups.map((group) => {
            if (group.comingSoon) {
              return (
                <div key={group.type} className="px-4 py-2.5 border-b border-white/5 last:border-b-0 opacity-50">
                  <span className="text-sm">{group.label}</span>
                  <span className="ml-2 text-[9px] font-mono bg-autoxium-blue px-1 py-0.5 rounded align-middle">SOON</span>
                </div>
              );
            }
            if (group.items.length === 0) return null;

            return (
              <div key={group.type} className="border-b border-white/5 last:border-b-0 py-2">
                <p className="px-4 pb-1 text-[10px] font-mono uppercase tracking-wider text-white/40">{group.label}</p>
                {group.items.map((item) => {
                  const flatIndex = flatItems.findIndex((f) => f.groupType === group.type && f.id === item.id);
                  const isActive = flatIndex === activeIndex;
                  return (
                    <button
                      type="button"
                      key={`${group.type}-${item.id}`}
                      onMouseEnter={() => setActiveIndex(flatIndex)}
                      onClick={() => selectItem({ ...item, groupType: group.type })}
                      className={cn(
                        'w-full text-left px-4 py-2 transition-colors',
                        isActive ? 'bg-white/10' : 'hover:bg-white/5'
                      )}
                      data-testid={`navbar-search-result-${group.type}`}
                    >
                      <div className="flex items-center gap-2">
                        <span className="text-sm font-medium truncate">{item.title}</span>
                        {item.category && (
                          <span className="text-[10px] font-mono text-autoxium-blue shrink-0">{item.category}</span>
                        )}
                      </div>
                      {item.excerpt && (
                        <p className="text-xs text-white/50 line-clamp-1 mt-0.5">{item.excerpt}</p>
                      )}
                    </button>
                  );
                })}
                {group.count > group.items.length && (
                  <button
                    type="button"
                    onClick={() => goToFullResults(trimmedQuery, group.type)}
                    className="w-full text-left px-4 pt-1.5 text-xs font-mono uppercase tracking-wider text-autoxium-blue hover:text-white"
                  >
                    View all results for &lsquo;{debouncedQuery}&rsquo; →
                  </button>
                )}
              </div>
            );
          })}

          {hasAnyResults && (
            <button
              type="button"
              onClick={() => goToFullResults()}
              className="w-full text-left px-4 py-2.5 text-xs font-mono uppercase tracking-wider text-autoxium-blue hover:text-white border-t border-white/10"
              data-testid="navbar-search-view-all"
            >
              View all results for &lsquo;{debouncedQuery}&rsquo; →
            </button>
          )}
        </div>
      )}
    </div>
  );
};

export default NavbarSearch;
